import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { CheckCircleOutline } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { useAtomValue } from "jotai";
import { sessionAtom } from "../../features/accounts/account.state";
import {
  PlanUpgradeLanding,
  takePlanUpgradeLanding,
} from "../../features/billing/upgradeReturn";
import { trackEvent } from "../../features/analytics/analytics";

/** "100 MB" under a gigabyte, "1 GB" from there; the account's `space` is MB. */
const storageText = (mb: number | undefined): string | null => {
  if (typeof mb !== "number" || mb <= 0) return null;
  return mb >= 1000 ? `${mb / 1000} GB` : `${mb} MB`;
};

/**
 * What the user just paid for, shown once on the page they land on.
 *
 * Until 2026-09-30 a plan change ended in a toast and a reload, and a paid-to-paid
 * upgrade ended in a reload alone: nothing named the plan, the new limits or the
 * credit balance. Two of the five traced day-one paid cancels then met a second
 * wall without ever having been told what the first payment bought (S34 § 8,
 * gap #4). `ChangePlan` writes the landing record as it reloads or redirects;
 * this reads it on the next page, so it works wherever the user is returned to.
 *
 * Copy and limits only. Nothing here touches billing.
 */
export const PlanUpgradeConfirmation = () => {
  const session = useAtomValue(sessionAtom);
  const [landing, setLanding] = useState<PlanUpgradeLanding | null>(null);

  useEffect(() => {
    const record = takePlanUpgradeLanding();
    if (!record) return;
    setLanding(record);
    trackEvent({
      type: "feature",
      category: "plan",
      action: "upgrade-confirmation-shown",
      label: record.to,
      metadata: { from: record.from, returned: record.returned },
      internal: true,
    });
  }, []);

  const account = session?.account;
  if (!landing || !account) return null;

  // The reload that precedes this refreshes the session, so its limits are the new
  // plan's. If the two still disagree (a slow webhook, an old tab), the numbers
  // would describe the old plan, so only the plan name is shown.
  const planName = account.planName ?? landing.to;
  const limitsAreCurrent =
    planName.toLowerCase() === landing.to.toLowerCase();
  const limits = account.limits;
  const credits = account.summary?.concierge?.credits;

  const lines: string[] = [];
  if (limitsAreCurrent && limits) {
    if (limits.items > 0) lines.push(`${limits.items} products`);
    if (limits.catalogs > 0) lines.push(`${limits.catalogs} catalogs`);
    if (limits.pageLimit > 0) {
      lines.push(`${limits.pageLimit} pages per catalog`);
    } else if (limits.pageLimit === -1) {
      lines.push("Unlimited pages per catalog");
    }
    const storage = storageText(limits.space);
    if (storage) lines.push(`${storage} image storage`);
    // `limits.users` counts seats BEYOND the owner (Free and Lite carry 0, Pro 1),
    // the same reading `Users.tsx` and `SubscriptionCard` use. Printed as people.
    const seats = (limits.users ?? 0) + 1;
    lines.push(`${seats} ${seats === 1 ? "user" : "users"}`);
  }
  const creditLine =
    limitsAreCurrent && credits
      ? `$${(credits.available ?? 0).toFixed(2)} AI credits available` +
        (credits.monthlyIncluded > 0
          ? `, $${credits.monthlyIncluded.toFixed(0)} included every month`
          : "")
      : null;

  const close = () => setLanding(null);

  return (
    <Dialog open onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CheckCircleOutline color="success" />
        Your {planName} plan is active
      </DialogTitle>
      <DialogContent>
        {lines.length > 0 ? (
          <>
            <Typography variant="body2" color="text.secondary">
              Your account now includes:
            </Typography>
            <Box component="ul" sx={{ mt: 0.5, mb: 1.5, pl: 2.5 }}>
              {lines.map((line) => (
                <Typography component="li" variant="body2" key={line}>
                  {line}
                </Typography>
              ))}
            </Box>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Your new limits apply from now on.
          </Typography>
        )}
        {creditLine && (
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {creditLine}.
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          {landing.returned
            ? "You are back where you left off."
            : "Thank you for upgrading."}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          component={RouterLink}
          to="/account/billing/subscription"
          onClick={close}
          color="inherit"
        >
          Plan details
        </Button>
        <Button variant="contained" onClick={close} autoFocus>
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};
